import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type MouseEvent } from "react";
import { createPortal } from "react-dom";
import { Download } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { apiFetchBlob } from "@/lib/api";
import { computeHorizontalMenuPanel, FLOATING_POPOVER_Z_INDEX, type AnchoredMenuPanelBox } from "@/lib/viewportAnchoredPopover";
import { cn } from "@/lib/utils";

import { gosiInvoiceMenuLabel, PENDING_SALES_ORDER_PDF_DOWNLOAD_TITLE, salesOrderDocumentMenuLabel } from "./salesOrderDownloadMenuLabels";

export interface SalesOrderDownloadInvoice {
  mvId: string;
  number: string;
}

function stopNavigation(event: MouseEvent | KeyboardEvent) {
  event.stopPropagation();
}

async function savePdf(url: string, fallbackName: string): Promise<void> {
  const result = await apiFetchBlob(url);
  const objectUrl = URL.createObjectURL(result.blob);
  const link = window.document.createElement("a");
  link.href = objectUrl;
  link.download = result.originalFilename || fallbackName;
  window.document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

export function SalesOrderDownloadMenu({
  orderId,
  orderNumber,
  invoices,
  canDownloadSalesOrderPdf,
  onError,
  density = "row",
  stopCardNavigation = false,
}: {
  orderId: number;
  orderNumber: string;
  invoices: SalesOrderDownloadInvoice[];
  canDownloadSalesOrderPdf: boolean;
  onError?: (message: string) => void;
  density?: "row" | "header";
  stopCardNavigation?: boolean;
}) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuBox, setMenuBox] = useState<AnchoredMenuPanelBox | null>(null);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const salesOrderLabel = salesOrderDocumentMenuLabel(orderNumber);

  const updateMenuPosition = useCallback(() => {
    const trigger = buttonRef.current;
    if (trigger == null) {
      setMenuBox(null);
      return;
    }
    setMenuBox(
      computeHorizontalMenuPanel({
        anchorRect: trigger.getBoundingClientRect(),
        menuWidth: 240,
        horizontalAlign: density === "header" ? "end" : "start",
        maxHeightCap: 320,
      }),
    );
  }, [density]);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [menuOpen, updateMenuPosition]);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      setMenuOpen(false);
    };
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  async function downloadDocument(key: string, url: string, fallbackName: string) {
    setDownloadingKey(key);
    setMenuOpen(false);
    try {
      await savePdf(url, fallbackName);
    } catch (error) {
      onError?.(error instanceof Error ? error.message : "Could not download the document.");
    } finally {
      setDownloadingKey(null);
    }
  }

  const menuItemClass = "block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-transparent";

  return (
    <div
      className="inline-flex"
      onClick={stopCardNavigation ? stopNavigation : undefined}
      onKeyDown={stopCardNavigation ? stopNavigation : undefined}
    >
      <Button
        ref={buttonRef}
        type="button"
        variant="secondary"
        size={density === "header" ? "md" : "sm"}
        className={density === "header" ? "h-10 w-10 px-0" : "h-8 w-8 px-0"}
        aria-label={`Download documents for ${salesOrderLabel}`}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        title="Download documents"
        isLoading={downloadingKey != null}
        onClick={(event) => {
          event.stopPropagation();
          setMenuOpen((open) => !open);
        }}
      >
        <Download className="h-4 w-4" aria-hidden="true" />
      </Button>
      {menuOpen && menuBox != null
        ? createPortal(
            <div
              ref={panelRef}
              className="fixed overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
              style={{
                left: menuBox.left,
                top: menuBox.top,
                width: menuBox.width,
                maxHeight: menuBox.maxHeight,
                transform: menuBox.transform,
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
              role="menu"
              aria-label={`Documents for ${salesOrderLabel}`}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                role="menuitem"
                className={menuItemClass}
                disabled={!canDownloadSalesOrderPdf || downloadingKey != null}
                title={canDownloadSalesOrderPdf ? salesOrderLabel : PENDING_SALES_ORDER_PDF_DOWNLOAD_TITLE}
                onClick={() => {
                  if (!canDownloadSalesOrderPdf) {
                    return;
                  }
                  void downloadDocument(
                    "sales-order",
                    `/api/sales-orders/${orderId}/pdf`,
                    `${salesOrderLabel}.pdf`,
                  );
                }}
              >
                {salesOrderLabel}
              </button>
              {invoices.map((invoice) => {
                const label = gosiInvoiceMenuLabel(invoice.number);
                return (
                  <button
                    key={invoice.mvId}
                    type="button"
                    role="menuitem"
                    className={cn(menuItemClass)}
                    disabled={downloadingKey != null}
                    title={label}
                    onClick={() => {
                      void downloadDocument(
                        invoice.mvId,
                        `/api/sales-orders/${orderId}/invoices/${encodeURIComponent(invoice.mvId)}/pdf`,
                        `${label}.pdf`,
                      );
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
